import { useEffect, useMemo, useState } from 'react';
import { peekCheckCounter, commitCheckNumbers } from '../../lib/checkNumbers';
import { ArrowLeft, ArrowRight, CalendarClock, CalendarDays, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  normFreq, toISO, buildAnchor, projectPeriods, buildRedatedStubs, scheduleCheck, suggestTemplateId, PERIODS,
} from '../../lib/paystubRedate';
import { RedatePreviewTable } from './RedatePreviewTable';

const FREQS = [['weekly', 'Weekly'], ['bi-weekly', 'Bi-weekly'], ['semi-monthly', 'Semi-monthly'], ['monthly', 'Monthly']];
const inputCls = 'w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:outline-none focus:border-sky-500';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</span>
    {children}
  </label>
);

const ModeButton = ({ active, onClick, icon: Icon, title, desc, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex-1 text-left p-4 rounded-xl border-2 transition-colors ${active ? 'border-sky-500 bg-sky-500/10' : 'border-zinc-800 bg-zinc-900 hover:border-zinc-600'}`}
  >
    <div className="flex items-center gap-2 text-white font-semibold"><Icon className="w-4 h-4 text-sky-400" />{title}</div>
    <p className="text-xs text-zinc-400 mt-1">{desc}</p>
  </button>
);

const MODE_INFO = {
  next: { icon: ArrowRight, title: 'Next stubs after this one', desc: 'Continue the same schedule forward from this stub (e.g. paid Sept 1 bi-weekly → Sept 15, Sept 29).' },
  latest: { icon: CalendarClock, title: 'Most recent stubs', desc: 'Continue the schedule forward and give the latest stubs up to the date you pick.' },
  specific: { icon: CalendarDays, title: 'Pick a specific pay date', desc: 'Set the exact pay date for the newest stub; older stubs step back by the pay frequency.' },
};

export default function PaystubRedatePanel({
  source, onBack, onApply, modes = ['latest', 'specific'], initialMode = 'latest', maxCount = 3, templateName = null, showHeader = true,
}) {
  const src = source.formData.paystubs[0];
  const [freq, setFreq] = useState(normFreq(source.formData.payFrequency));
  const [anchorDates, setAnchorDates] = useState({ payDate: src.payDate, periodStart: src.periodStart, periodEnd: src.periodEnd });
  const [mode, setMode] = useState(initialMode);
  const [keepTemplate, setKeepTemplate] = useState(true);
  const [asOf, setAsOf] = useState(toISO(new Date()));
  const [targetDate, setTargetDate] = useState('');
  const [count, setCount] = useState(2);

  const company = (source.formData.companyName || '').trim();
  const [checkFloor, setCheckFloor] = useState(0);
  useEffect(() => {
    if (company) peekCheckCounter(company).then(setCheckFloor).catch(() => {});
  }, [company]);

  const anchor = useMemo(() => buildAnchor(anchorDates, freq), [anchorDates, freq]);
  const stubs = useMemo(() => {
    if (!anchor) return [];
    const periods = projectPeriods({ anchor, freq, mode, asOf, targetDate, count });
    return buildRedatedStubs(src, periods, checkFloor);
  }, [anchor, freq, mode, asOf, targetDate, count, src, checkFloor]);
  const check = mode === 'specific' && anchor && targetDate ? scheduleCheck(anchor, new Date(`${targetDate}T12:00:00`), freq) : null;

  const apply = () => {
    const latest = stubs[0];
    if (company) commitCheckNumbers(company, stubs.map((s) => s.checkNumber)).catch(() => {});
    const gross = Number(src.current?.grossPay) || 0;
    onApply({
      ...source.formData,
      payFrequency: freq,
      numberOfStubs: stubs.length,
      paystubs: stubs,
      selectedYear: Number(latest.payDate.slice(0, 4)),
      stubNumberInYear: latest.stubNumberInYear,
      payEntryMode: 'per-check',
      perCheckType: 'gross',
      perCheckAmount: gross,
      annualSalary: Math.round(gross * (PERIODS[freq] || 26) * 100) / 100 || source.formData.annualSalary,
      preserveUploadedAmounts: true,
      checkNumbersV2: true,
      selectedTemplate: templateName ? source.formData.selectedTemplate : suggestTemplateId(source.extractedData?.layout_info),
    }, keepTemplate);
  };

  return (
    <div className="space-y-6" data-testid="redate-panel">
      {showHeader && (<>
      <Button variant="ghost" onClick={onBack} className="ml-12 md:ml-0 text-zinc-400" data-testid="redate-back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back
      </Button>
      <div>
        <h1 className="text-2xl font-bold text-white">Choose the new dates</h1>
        <p className="text-sm text-zinc-400 mt-1">
          {source.formData.employeeName || 'Employee'} · {source.formData.companyName || 'Company'} — per-check amounts stay the same; dates move forward on the same schedule and YTD is recalculated.
        </p>
      </div>
      </>)}

      <section className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">{templateName ? 'Starting stub' : 'Uploaded stub (fix anything the AI misread)'}</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Pay date"><input type="date" className={inputCls} value={anchorDates.payDate || ''} onChange={(e) => setAnchorDates((p) => ({ ...p, payDate: e.target.value }))} data-testid="redate-src-paydate" /></Field>
          <Field label="Period start"><input type="date" className={inputCls} value={anchorDates.periodStart || ''} onChange={(e) => setAnchorDates((p) => ({ ...p, periodStart: e.target.value }))} data-testid="redate-src-start" /></Field>
          <Field label="Period end"><input type="date" className={inputCls} value={anchorDates.periodEnd || ''} onChange={(e) => setAnchorDates((p) => ({ ...p, periodEnd: e.target.value }))} data-testid="redate-src-end" /></Field>
          <Field label="Pay frequency">
            <select className={inputCls} value={freq} onChange={(e) => setFreq(e.target.value)} data-testid="redate-frequency">
              {FREQS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>
      </section>

      <div className="flex flex-col md:flex-row gap-3">
        {modes.map((m) => (
          <ModeButton key={m} testid={`redate-mode-${m}`} active={mode === m} onClick={() => setMode(m)}
            icon={MODE_INFO[m].icon} title={MODE_INFO[m].title} desc={MODE_INFO[m].desc} />
        ))}
      </div>

      <section className="grid grid-cols-2 gap-3 max-w-md">
        {mode === 'next' ? (
          <div className="text-xs text-zinc-400 self-end pb-2">New stubs start one pay period after the starting stub.</div>
        ) : mode === 'latest' ? (
          <Field label="Latest pay date on or before"><input type="date" className={inputCls} value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="redate-asof" /></Field>
        ) : (
          <Field label="New pay date (most recent stub)"><input type="date" className={inputCls} value={targetDate} onChange={(e) => setTargetDate(e.target.value)} data-testid="redate-target-date" /></Field>
        )}
        <Field label="How many stubs">
          <select className={inputCls} value={count} onChange={(e) => setCount(Number(e.target.value))} data-testid="redate-count">
            {Array.from({ length: maxCount }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>
      </section>

      {check && (
        <div className={`flex items-center gap-2 text-sm ${check.onSchedule ? 'text-emerald-300' : 'text-amber-300'}`} data-testid="redate-schedule-check">
          {check.onSchedule ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {check.onSchedule ? 'This date is on the uploaded pay schedule.' : `Off the uploaded schedule — nearest scheduled pay date is ${check.nearest}.`}
          {!check.onSchedule && (
            <button type="button" className="underline text-sky-300" onClick={() => setTargetDate(check.nearest)} data-testid="redate-snap-btn">Use it</button>
          )}
        </div>
      )}

      {stubs.length > 0 ? <RedatePreviewTable stubs={stubs} /> : <p className="text-sm text-zinc-500">Pick a date to preview the new stubs.</p>}

      {templateName && (
        <div className="flex flex-col sm:flex-row gap-3" data-testid="redate-template-choice">
          <ModeButton testid="redate-keep-template" active={keepTemplate} onClick={() => setKeepTemplate(true)} icon={CheckCircle2}
            title={`Keep the same template (${templateName})`} desc="Goes straight to Paystub Details to review numbers, then generate." />
          <ModeButton testid="redate-change-template" active={!keepTemplate} onClick={() => setKeepTemplate(false)} icon={CalendarDays}
            title="Choose a different template" desc="Opens the template gallery first." />
        </div>
      )}

      <Button onClick={apply} disabled={!stubs.length} className="w-full h-14 bg-sky-600 hover:bg-sky-500 text-white text-lg font-semibold" data-testid="redate-apply-btn">
        {templateName && keepTemplate ? 'Continue — Review Numbers' : 'Continue — Pick Template'} <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );
}
